import { useCallback, useEffect, useRef, useState } from 'react'

// CoinGecko platform IDs for each chain
const COINGECKO_PLATFORMS: { [chainId: number]: string } = {
  137: 'polygon-pos',
  56: 'binance-smart-chain',
  42161: 'arbitrum-one'
}

// DeFiLlama chain identifiers
const DEFILLAMA_CHAINS: { [chainId: number]: string } = {
  137: 'polygon',
  56: 'bsc',
  42161: 'arbitrum'
}

export interface TokenPrices {
  [tokenAddress: string]: number // USD price
}

export interface UseTokenPricesResult {
  prices: TokenPrices
  loading: boolean
  error: Error | null
  refetch: () => void
}

// Cache prices for 60 seconds
const CACHE_DURATION = 60 * 1000
const priceCache: {
  prices: TokenPrices
  timestamp: number
} = {
  prices: {},
  timestamp: 0
}

async function fetchPricesFromDeFiLlama(tokensByChain: {
  [chainId: number]: string[]
}): Promise<TokenPrices> {
  const prices: TokenPrices = {}

  // Build the coins parameter for DeFiLlama
  const coins: string[] = []
  for (const [chainId, tokens] of Object.entries(tokensByChain)) {
    const chain = DEFILLAMA_CHAINS[parseInt(chainId)]
    if (!chain) continue
    for (const token of tokens) {
      coins.push(`${chain}:${token}`)
    }
  }

  if (coins.length === 0) return prices

  try {
    const url = `https://coins.llama.fi/prices/current/${coins.join(',')}`
    const response = await fetch(url)
    if (!response.ok) throw new Error(`DeFiLlama API error: ${response.status}`)

    const data = await response.json()

    // Parse response
    for (const [key, value] of Object.entries(data.coins || {})) {
      // key format: "polygon:0x..."
      const parts = key.split(':')
      if (parts.length === 2) {
        const address = parts[1].toLowerCase()
        const priceData = value as { price?: number }
        if (priceData.price) {
          prices[address] = priceData.price
        }
      }
    }
  } catch (error) {
    console.error('DeFiLlama price fetch failed:', error)
  }

  return prices
}

async function fetchPricesFromCoinGecko(tokensByChain: {
  [chainId: number]: string[]
}): Promise<TokenPrices> {
  const prices: TokenPrices = {}

  for (const [chainId, tokens] of Object.entries(tokensByChain)) {
    const platform = COINGECKO_PLATFORMS[parseInt(chainId)]
    if (!platform || tokens.length === 0) continue

    try {
      const url = `https://api.coingecko.com/api/v3/simple/token_price/${platform}?contract_addresses=${tokens.join(
        ','
      )}&vs_currencies=usd`
      const response = await fetch(url)
      if (!response.ok) {
        console.warn(`CoinGecko API error for ${platform}: ${response.status}`)
        continue
      }

      const data = await response.json()

      for (const [address, priceData] of Object.entries(data)) {
        const pd = priceData as { usd?: number }
        if (pd.usd) {
          prices[address.toLowerCase()] = pd.usd
        }
      }
    } catch (error) {
      console.error(`CoinGecko price fetch failed for ${platform}:`, error)
    }
  }

  return prices
}

export function useTokenPrices(tokensByChain: {
  [chainId: number]: string[]
}): UseTokenPricesResult {
  const [prices, setPrices] = useState<TokenPrices>({})
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)
  const fetchingRef = useRef(false)

  const fetchPrices = useCallback(async () => {
    // Check cache
    const now = Date.now()
    if (now - priceCache.timestamp < CACHE_DURATION && Object.keys(priceCache.prices).length > 0) {
      setPrices(priceCache.prices)
      setLoading(false)
      return
    }

    // Avoid duplicate fetches
    if (fetchingRef.current) return
    fetchingRef.current = true

    setLoading(true)
    setError(null)

    try {
      // Try DeFiLlama first (more reliable, no rate limits)
      let fetchedPrices = await fetchPricesFromDeFiLlama(tokensByChain)

      // Fallback to CoinGecko for any missing prices
      const missingTokens: { [chainId: number]: string[] } = {}
      for (const [chainId, tokens] of Object.entries(tokensByChain)) {
        // eslint-disable-next-line no-loop-func
        const missing = tokens.filter(t => !fetchedPrices[t.toLowerCase()])
        if (missing.length > 0) {
          missingTokens[parseInt(chainId)] = missing
        }
      }

      if (Object.keys(missingTokens).length > 0) {
        const coingeckoPrices = await fetchPricesFromCoinGecko(missingTokens)
        fetchedPrices = { ...fetchedPrices, ...coingeckoPrices }
      }

      // Update cache
      priceCache.prices = fetchedPrices
      priceCache.timestamp = now

      setPrices(fetchedPrices)
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch prices'))
    } finally {
      setLoading(false)
      fetchingRef.current = false
    }
  }, [tokensByChain])

  useEffect(() => {
    const tokenCount = Object.values(tokensByChain).flat().length
    if (tokenCount > 0) {
      fetchPrices()
    } else {
      setLoading(false)
    }
  }, [fetchPrices, tokensByChain])

  return {
    prices,
    loading,
    error,
    refetch: fetchPrices
  }
}

// Helper to extract unique tokens from pair data
export function extractUniqueTokens(pairData: {
  [pairAddress: string]: { chainId: number; token0Address: string; token1Address: string }
}): { [chainId: number]: string[] } {
  const tokensByChain: { [chainId: number]: Set<string> } = {}

  for (const pair of Object.values(pairData)) {
    if (!tokensByChain[pair.chainId]) {
      tokensByChain[pair.chainId] = new Set()
    }
    tokensByChain[pair.chainId].add(pair.token0Address.toLowerCase())
    tokensByChain[pair.chainId].add(pair.token1Address.toLowerCase())
  }

  const result: { [chainId: number]: string[] } = {}
  for (const [chainId, tokens] of Object.entries(tokensByChain)) {
    result[parseInt(chainId)] = Array.from(tokens)
  }

  return result
}
